export { data }
export type { Data }

import type { PageContextClient } from 'vike/types'
import { describeUnexpectedError } from '../../components/describeError.ts'
import { readLoginToken } from './loginHash.ts'
import { onLoadDashboard } from './+Page.telefunc.ts'
import type { LoadResult } from './+Page.telefunc.ts'

// Client-only (`.client.ts`) by necessity: the magic-login credential arrives in the URL
// *fragment* (`/#login:<token>`), which browsers never send to the server, and the `auth`
// cookie is set by a client-side POST. So the whole sign-in bootstrap has to run here,
// before the dashboard load — a server-side `+data` literally couldn't see the token.
//
// Two payoffs over the previous in-component `useEffect`:
//   1. It's a Vike routing hook, not a React effect, so it fires exactly ONCE per
//      navigation — no StrictMode dev double-invoke, no `cancelled`-flag bookkeeping.
//   2. The cookie is set + auth resolved before the first React render, so there's no
//      flash of <Login> while the server is still setting the cookie.
// `LoadResult` is what `onLoadDashboard` returns (the two signed-in/out cases); `data()`
// reuses it (so a new field on the load result can't drift from the page's view type),
// adding a derived `linkExpired` flag plus the client-only `{ error }` case for a failed load.
type Data = (LoadResult & { linkExpired: boolean }) | { error: string }

async function data(_pageContext: PageContextClient): Promise<Data> {
  const attempted = await consumeLoginHash()
  try {
    const res = await onLoadDashboard()
    // If we just consumed a `#login:` token but the load still reports logged-out, the link
    // didn't work — flag it so <Login> can cue "link no longer valid". Derived fresh each call
    // (no persisted marker): a successful load yields `false`, so a later sign-out can't replay
    // the notice.
    //
    // Known gap (accepted, cosmetic): the notice is reliably shown only on the HARD-load path (link
    // clicked from an email). On the SOFT paste-into-an-open-tab path (`useReloadOnLoginHash` →
    // `reload()` in +Page.tsx), `data()` re-runs once more AFTER `useStripConsumedLoginHash` strips
    // the fragment, and that tokenless re-eval recomputes `linkExpired` as false — overwriting this
    // `true`. So an INVALID link pasted into an already-open tab lands on a bare <Login> with no
    // notice. Unreachable today (no link-expiry is enforced — see README "Magic-link freshness …
    // deferred" — so the only invalid links are tampered ones, and a VALID paste still signs in), and
    // not worked around: caching the live token re-POSTs it, and a persisted `attempted` flag would
    // violate the no-marker rule just above (it'd replay the notice after a sign-out).
    return { ...res, linkExpired: attempted && !res.loggedIn }
  } catch (err) {
    // `onLoadDashboard` returns `{ loggedIn: false }` for the logged-out branch, so a throw
    // here is a genuine failure (network / telefunc). console.error → Sentry is intended.
    console.error('onLoadDashboard failed:', err)
    return { error: describeUnexpectedError(err) }
  }
}

// Exchange the magic-login token for the HttpOnly `auth` cookie. The token was already pulled out
// of the URL fragment and the fragment stripped at boot by `captureLoginToken` (`+client.ts`);
// `readLoginToken` returns that captured value. No-op when there was no `#login:` fragment — the
// returning-visitor path relies on the cookie the browser already holds. We don't inspect the
// POST's status: `onLoadDashboard` is the source of truth for "is this customer signed in?", so a
// rejected token simply yields `{ loggedIn: false }`.
async function consumeLoginHash(): Promise<boolean> {
  const token = readLoginToken()
  if (!token) return false
  try {
    await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
    })
  } catch (err) {
    // Transient / offline. We still return `true` (a token WAS consumed); `onLoadDashboard`
    // will report logged-out (cookie unset), so <Login> still cues "link no longer valid".
    // `fetch` rejects only on transport failure (offline/DNS/TLS), never on a 401/4xx (those
    // resolve normally) — so this is always an expected client-network blip, not a bug. Use
    // `console.info` (a breadcrumb): `captureConsoleIntegration` (see +client.ts) promotes BOTH
    // `warn` AND `error` to Sentry issues, so the earlier `console.warn` did NOT keep this out
    // of the issue stream — only `info`/`log` stay breadcrumbs.
    console.info('/api/auth/login failed:', err)
  }
  return true
}
